import type { GetApiGroupParams, GroupViewDTO } from '@smartsash/api-client';
import type { TableMeta } from '@tanstack/react-table';
import type { z } from 'zod';

import { ADMIN_LIST_SEARCH_DEFAULTS, createAdminListSearchSchema } from '@/router/listSearch';
import {
  buildFilterParams,
  collectSearchTerms,
  sortParams,
  type FilterParamMap,
} from '@/utils/listQueryParams';

export const groupsSearchSchema = createAdminListSearchSchema();

export type GroupsSearch = z.infer<typeof groupsSearchSchema>;

/** The builder field ids the filtering-foundation API can narrow on. */
export const GROUPS_INSTITUTION_FIELD = 'institution';
export const GROUPS_CITY_FIELD = 'city';

/**
 * Column id → the endpoint's `sortBy` value.
 *
 * `GET /api/Group` whitelists `name`, `address` and `createdAt` only. The laboratory count is
 * pointedly absent: `GroupViewDTO` still has no `amountLabs`, so the column is a client-side join
 * and the server has nothing to order by.
 */
export const GROUPS_SORT_KEYS: Readonly<Record<string, string>> = {
  name: 'name',
  address: 'address',
};

/**
 * The builder fields whose `Equals` condition reaches `GetApiGroupParams` (HS-869 follow-up). The
 * Institution field is the table's own column, re-vocabularied in the filter so it carries
 * institution ids rather than the names it renders; City is not a column at all and is appended as
 * an extra field.
 */
export const GROUPS_FILTER_PARAMS: FilterParamMap<GetApiGroupParams> = {
  [GROUPS_INSTITUTION_FIELD]: { kind: 'multi', param: 'institutionId' },
  [GROUPS_CITY_FIELD]: { kind: 'multi', param: 'city' },
};

/**
 * The search that links an institution's "View groups" action to this list, scoped to that
 * institution — mirrors `laboratoriesFilteredByInstitution`/`devicesFilteredBy`/
 * `usersFilteredByInstitution` (HS-1376).
 */
export function groupsFilteredByInstitution(institutionId: string) {
  return {
    ...ADMIN_LIST_SEARCH_DEFAULTS,
    filters: [
      { field: GROUPS_INSTITUTION_FIELD, operator: 'eq' as const, values: [institutionId] },
    ],
  };
}

/**
 * Turn the URL's `page`/`q`/`sort`/`filters` into `GetApiGroupParams` (HS-869 follow-up).
 *
 * Shared by the screen's own `query` and `GroupsFilter`'s cache read: both have to build the exact
 * same params, or the filter panel would read a different cache entry than the table renders from.
 */
export function groupsQueryParams(search: GroupsSearch): GetApiGroupParams {
  const conditions = search.filters ?? [];
  const searchTerms = collectSearchTerms(conditions, search.q ?? '');
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...sortParams<GetApiGroupParams>(search.sort, GROUPS_SORT_KEYS),
    ...(searchTerms ? { search: searchTerms } : {}),
    ...buildFilterParams(conditions, GROUPS_FILTER_PARAMS),
  };
}

/**
 * The text each column holds for a row — the one source the table, the client-scope search and
 * sort, and the filter builder's vocabulary all read (HS-869).
 *
 * `institution` and `laboratories` take the table's `meta`: `GroupViewDTO` carries only
 * `institutionId`, and neither the institution name nor the laboratory count backs a field on the
 * DTO directly — both are resolved client-side by joining the institution and lab lists
 * (FK-join policy). A column reader cannot run a query, so the maps arrive through
 * `list.useTableMeta`.
 *
 * Lives apart from the screen definition so the filter component can share it without importing
 * the definition that renders the filter.
 */
export const groupColumnText = {
  name: (row: GroupViewDTO) => `${row.name ?? ''} ${row.accountNumber ?? ''}`.trim(),
  institution: (row: GroupViewDTO, meta: TableMeta<GroupViewDTO> | undefined) =>
    (row.institutionId == null ? undefined : meta?.institutionNames?.get(row.institutionId)) ?? '',
  address: (row: GroupViewDTO) => row.address ?? '',
  manager: (row: GroupViewDTO) => `${row.contact ?? ''} ${row.email ?? ''}`.trim(),
  laboratories: (row: GroupViewDTO, meta: TableMeta<GroupViewDTO> | undefined) =>
    String((row.id == null ? undefined : meta?.labCountByGroupId?.get(row.id)) ?? 0),
} as const;

/**
 * Every column id is also a header id: HS-1241 removed the last column that had no entry here.
 *
 * That was the Figma 432:9647 "Type" badge (Building/Department/Tenant) — a distinction the API
 * dropped in Q-10/HS-939, so `GroupViewDTO` never had a field to back it and the column rendered a
 * placeholder dash. QA read an always-empty column as a defect rather than a placeholder, so it is
 * gone until the backend exposes a real value.
 */
export type GroupColumnId = keyof typeof groupColumnText;
